import { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'

interface Props {
  onResult: (name: string, barcode: string) => void
  onClose:  () => void
}

interface OpenFoodFactsProduct {
  product_name?: string
  product_name_en?: string
  brands?: string
  quantity?: string
}

async function lookupBarcode(barcode: string): Promise<string | null> {
  // 1. Fetch raw product data from Open Food Facts
  let rawName  = ''
  let brandName = ''
  try {
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`,
      { signal: AbortSignal.timeout(5000) },
    )
    if (res.ok) {
      const data = await res.json()
      if (data.status === 1) {
        const p: OpenFoodFactsProduct = data.product ?? {}
        rawName   = p.product_name_en || p.product_name || ''
        brandName = p.brands || ''
      }
    }
  } catch {
    // ignore — fall through to Claude
  }

  // 2. Ask Claude to produce a clean pantry name
  try {
    const res = await fetch('/api/claude-barcode', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ barcode, rawName: rawName.trim(), brandName: brandName.trim() }),
      signal:  AbortSignal.timeout(8000),
    })
    if (res.ok) {
      const data = await res.json()
      if (data.name) return data.name
    }
  } catch {
    // ignore
  }

  // 3. Fall back to raw Open Food Facts name if Claude failed
  return rawName.trim() || null
}

export default function BarcodeScanner({ onResult, onClose }: Props) {
  const videoRef   = useRef<HTMLVideoElement>(null)
  const readerRef  = useRef<BrowserMultiFormatReader | null>(null)
  const [status,   setStatus]   = useState<'scanning' | 'found' | 'error'>('scanning')
  const [message,  setMessage]  = useState('Point camera at a barcode')
  const [foundName, setFoundName] = useState('')

  useEffect(() => {
    let cancelled = false
    const reader = new BrowserMultiFormatReader()
    readerRef.current = reader

    let stopFn: (() => void) | null = null

    reader.decodeFromVideoDevice(undefined, videoRef.current!, async (result, _err) => {
      if (cancelled) return

      if (result) {
        const barcode = result.getText()
        setStatus('found')
        setMessage('Looking up product…')

        const name = await lookupBarcode(barcode)

        if (cancelled) return

        if (name) {
          setFoundName(name)
          setMessage(`Found: ${name}`)
          setTimeout(() => {
            if (!cancelled) onResult(name, barcode)
          }, 800)
        } else {
          setStatus('error')
          setMessage('Product not found — try typing the name')
          setTimeout(() => {
            if (!cancelled) onClose()
          }, 2000)
        }
      }
    }).then(controls => {
      stopFn = () => controls.stop()
    }).catch(_err => {
      if (!cancelled) {
        setStatus('error')
        setMessage('Camera not available — check permissions')
      }
    })

    return () => {
      cancelled = true
      try { stopFn?.() } catch (_e) {}
    }
  }, [onResult, onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80">
        <p className="text-white text-sm font-medium">Scan barcode</p>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white text-sm"
        >
          ✕
        </button>
      </div>

      {/* Viewfinder */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          playsInline
        />

        {/* Dimming overlay with cutout */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />

          {/* Scan window */}
          <div
            className="relative z-10 w-72 h-44 rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }}
          >
            {/* Animated scan line */}
            {status === 'scanning' && (
              <div className="absolute inset-x-0 top-0 h-0.5 bg-[#6B9E78] scan-line" />
            )}

            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#6B9E78] rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#6B9E78] rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#6B9E78] rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#6B9E78] rounded-br-lg" />
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div
        className="px-6 py-5 text-center transition-colors"
        style={{
          background: status === 'found'  ? '#1a2e1d'
                    : status === 'error'  ? '#2e1a1a'
                    : '#111',
        }}
      >
        {status === 'found' && foundName ? (
          <>
            <p className="text-[#6B9E78] text-xs font-semibold uppercase tracking-wide mb-1">
              Product found
            </p>
            <p className="text-white font-semibold">{foundName}</p>
          </>
        ) : (
          <p className={`text-sm ${status === 'error' ? 'text-[#C25B4E]' : 'text-[#B0B0B0]'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  )
}
